import React from 'react';
import { reverse } from 'fp-ts/Array';
import {
  TCreditsConsumptionInfoResponse,
  TSubscriptionDetailsResponse,
} from '@/features/credits/apiSlice';
import { TConsumptionCategory } from '@/features/credits/types';
import {
  COLOR_TO_CONSUMPTION_CATEGORY,
  CONSUMPTION_CATEGORY_HOVER_EVENT,
  CONSUMPTION_CATEGORY_TO_COLORS,
  CONSUMPTION_CATEGORY_TO_DISPLAY_NAME,
} from '@/features/credits/config';
import {
  CHART_DATA,
  CHART_OPTIONS,
} from '@/features/credits/components/HowYouUseCredits/HowYouUseCredits.config';

export const useChartConfiguration = (
  subscriptionDetails: Maybe<TSubscriptionDetailsResponse>,
  creditsConsumptionInfo: Maybe<TCreditsConsumptionInfoResponse>,
) => {
  const lastHoveredCategoryRef =
    React.useRef<Maybe<TConsumptionCategory>>(undefined);

  const notReady = !subscriptionDetails || !creditsConsumptionInfo;

  return React.useMemo(() => {
    if (notReady) return {};

    const creditsConsumptionInfoASC = reverse(
      creditsConsumptionInfo.consumption,
    );

    const totalConsumedCredits = creditsConsumptionInfo.totalConsumedCredits;

    const leftCredits = subscriptionDetails.totalCredits - totalConsumedCredits;

    const percentageData = creditsConsumptionInfoASC
      .map(
        ({ consumedCredits }) =>
          (consumedCredits * 100) / subscriptionDetails.totalCredits,
      )
      .concat(
        Math.max((leftCredits * 100) / subscriptionDetails.totalCredits, 0),
      );

    const chartColors = creditsConsumptionInfoASC
      .map(
        ({ categoryName }) => CONSUMPTION_CATEGORY_TO_COLORS[categoryName].main,
      )
      .concat('#F4F4F4');

    return {
      data: {
        ...CHART_DATA,
        labels: creditsConsumptionInfoASC.map(
          ({ categoryName }) =>
            CONSUMPTION_CATEGORY_TO_DISPLAY_NAME[categoryName],
        ),
        datasets: [
          {
            ...CHART_DATA.datasets[0],
            data: percentageData,
            backgroundColor: chartColors,
            hoverBackgroundColor: chartColors,
          },
        ],
      },
      options: {
        ...CHART_OPTIONS,
        onHover: (event: any, chartElements: any[]) => {
          if (chartElements.length) {
            const activityBeingHovered: Maybe<TConsumptionCategory> =
              COLOR_TO_CONSUMPTION_CATEGORY[
                chartElements[0].element.options.backgroundColor
              ];

            if (activityBeingHovered !== lastHoveredCategoryRef.current) {
              window.dispatchEvent(
                new CustomEvent(CONSUMPTION_CATEGORY_HOVER_EVENT, {
                  detail: {
                    category: activityBeingHovered,
                  },
                }),
              );
            }

            lastHoveredCategoryRef.current = activityBeingHovered;
          } else {
            if (lastHoveredCategoryRef.current !== undefined) {
              window.dispatchEvent(
                new CustomEvent(CONSUMPTION_CATEGORY_HOVER_EVENT),
              );
            }
            lastHoveredCategoryRef.current = undefined;
          }

          if (
            chartElements.length === 0 ||
            chartElements[0].index === percentageData.length - 1
          ) {
            event.native.target.style.cursor = 'default';
          } else {
            event.native.target.style.cursor = 'pointer';
          }
        },
        plugins: {
          ...CHART_OPTIONS.plugins,
          tooltip: {
            ...CHART_OPTIONS.plugins.tooltip,
            enabled: (obj: any) => {
              if (obj.type !== 'tooltip') return true;
              if (obj.tooltipItems.length === 0) return false;
              return (
                obj.tooltipItems[0].dataIndex !== percentageData.length - 1
              );
            },
          },
        },
      },
      totalConsumedCredits,
    };
  }, [
    JSON.stringify(subscriptionDetails),
    JSON.stringify(creditsConsumptionInfo),
  ]);
};
